"use client";

import { Asterisk, Loader, Send } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ContactFormFields() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="rounded-md border border-dashed border-orange-500 bg-white p-10 text-center">
        <Asterisk className="mx-auto mb-4 size-8 text-orange-500" strokeWidth={1.25} />
        <p className="font-default text-2xl text-slate-900">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-md border-[1.5px] border-slate-900 bg-transparent text-slate-900 hover:bg-slate-900 hover:text-white">
          Send another message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="name" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-slate-500">
                Full Name
              </FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="h-14 border-[1.5px] border-slate-200 bg-slate-50 px-4 text-base text-slate-900 placeholder:text-slate-400 focus-visible:border-orange-500 focus-visible:ring-orange-500/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-slate-500">
                Email Address
              </FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="h-14 border-[1.5px] border-slate-200 bg-slate-50 px-4 text-base text-slate-900 placeholder:text-slate-400 focus-visible:border-orange-500 focus-visible:ring-orange-500/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-slate-500">
                Phone (optional)
              </FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+1 (555) 000-0000" className="h-14 border-[1.5px] border-slate-200 bg-slate-50 px-4 text-base text-slate-900 placeholder:text-slate-400 focus-visible:border-orange-500 focus-visible:ring-orange-500/30" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="subject" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-slate-500">
                Inquiry Type
              </FormLabel>
              <FormControl>
                <Select onValueChange={field.onChange} value={field.value || ""}>
                  <SelectTrigger className="h-14 w-full border-[1.5px] border-slate-200 bg-slate-50 px-4 text-base text-slate-900 focus-visible:border-orange-500 focus-visible:ring-orange-500/30">
                    <SelectValue placeholder="Select an option" />
                  </SelectTrigger>
                  <SelectContent className="rounded-md border-slate-200 bg-white">
                    <SelectItem value="support">Technical Support</SelectItem>
                    <SelectItem value="sales">Enterprise Sales</SelectItem>
                    <SelectItem value="partnership">Partnership Inquiry</SelectItem>
                    <SelectItem value="general">General Feedback</SelectItem>
                  </SelectContent>
                </Select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-default text-[0.7rem] uppercase tracking-[0.18em] text-slate-500">
              Your Message
            </FormLabel>
            <FormControl>
              <Textarea rows={6} placeholder="How can VPNGENIX assist you today?" className="min-h-40 border-[1.5px] border-slate-200 bg-slate-50 px-4 py-3 text-base text-slate-900 placeholder:text-slate-400 focus-visible:border-orange-500 focus-visible:ring-orange-500/30" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="newsletter" render={({
      field
    }) => <FormItem className="flex flex-row items-center gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value || false} onCheckedChange={field.onChange} className="size-5 rounded-md border-[1.5px] border-orange-500 data-[state=checked]:bg-orange-500 data-[state=checked]:border-orange-500" />
            </FormControl>
            <FormLabel className="!mt-0 cursor-pointer text-sm font-normal text-slate-900">
              Subscribe to our monthly security newsletter
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="rounded-md border border-red-500/40 bg-red-50 p-4">
          <p className="text-sm text-red-600">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-md bg-orange-500 py-6 font-default text-sm uppercase tracking-[0.14em] text-white shadow-md transition-colors duration-240 hover:bg-orange-600 focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:ring-offset-2">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" strokeWidth={1.5} />
            Sending...
          </> : <>
            Send Message
            <Send className="ml-2 size-4" strokeWidth={1.5} />
          </>}
      </Button>
    </Form>;
}
export default function Contact() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section data-section-id="1513" id="contact" className="relative isolate overflow-hidden bg-white py-28 md:py-36">
      <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-slate-200" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-slate-200" />

      <div ref={ref} className={`relative mx-auto max-w-[1400px] px-6 transition-all duration-700 ease-out md:px-12 ${isVisible ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`}>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-12 md:gap-10">
          <div className="md:col-span-4">
            <div className="flex items-center gap-2 font-default text-[0.7rem] uppercase tracking-[0.18em] text-slate-500">
              <span>&sect;</span>
              <Asterisk className="size-3" strokeWidth={1.5} />
              <span>04 &mdash; Get in Touch</span>
            </div>
            <h2 className="mt-5 font-default text-[clamp(2.25rem,4.6vw,4.25rem)] font-bold leading-[1.1] tracking-[-0.02em] text-slate-900">
              Secure your{" "}
              <span className="relative inline-block text-orange-500">
                digital future.
              </span>
            </h2>
          </div>

          <div className="md:col-span-8 md:pt-2">
            <p className="font-default text-[clamp(1.125rem,1.2vw,1.25rem)] leading-[1.6] text-slate-600">
              Our team of cybersecurity experts is ready to help you navigate the complexities of digital privacy. Reach out for enterprise solutions, technical support, or general inquiries.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 font-default text-xs text-slate-500">
              <span>Global Headquarters, USA</span>
              <Asterisk className="size-3 text-orange-500" strokeWidth={1.5} />
              <span>support@vpngenix.com</span>
            </div>
          </div>
        </div>

        <div className="mt-12 border border-slate-200 bg-white p-7 shadow-sm md:p-14">
          <FormProvider formKey="contact_form_saas_tools_saas_genix" formId="contact_form_saas_tools_saas_genix" sectionName="contact-us" defaultValues={{
          name: "",
          email: "",
          phone: "",
          subject: "",
          message: "",
          newsletter: false
        }} validationRules={{
          name: {
            required: true,
            minLength: 2,
            maxLength: 80
          },
          email: {
            required: true,
            pattern: "email"
          },
          phone: {
            required: false
          },
          subject: {
            required: false
          },
          message: {
            required: true,
            minLength: 10
          },
          newsletter: {
            required: false
          }
        }} submitText="Send Message" successMessage="Message received. Our security team will respond within 24 hours." errorMessage="Submission failed. Please check your connection and try again.">
            <ContactFormFields />
          </FormProvider>
        </div>

        <p className="mt-6 text-center font-default text-xs text-slate-500">
          <Asterisk className="mr-1 inline size-3" strokeWidth={1.5} />
          Your privacy is our priority. We never share your contact information.
        </p>
      </div>
    </section>;
}